<template>
    <div class="body v2-list-page">
        <div class="order-page-container">
            <VisitFilters title="Requisições" :filters="filters" :fields="filterFields()" @filtrar="filtrar" @limpar="limpar">
                <template slot="actions">
                    <a-button type="primary" @click="showNew = true">
                        <i class="fa-solid fa-plus"></i>
                        Nova requisição
                    </a-button>
                </template>
            </VisitFilters>

            <div class="page-section">
                <a-card :bordered="false" style="width: 100%" class="table-card" :style="selectedIds.length && { marginBottom: '60px' }">
                    <div class="table-container">
                        <a-table
                            size="small"
                            row-key="id"
                            :row-selection="rowSelection"
                            :columns="columns"
                            :data-source="rows"
                            :loading="loading"
                            :pagination="pagination"
                            :scroll="{ x: 'max-content' }"
                            :locale="{ emptyText: 'Nenhuma requisição encontrada.' }"
                            @change="tableChange"
                        >
                            <template slot="provider" slot-scope="text, row">
                                <div class="col-fornecedor">
                                    <strong :title="row.provider ? row.provider.name : ''">
                                        {{ row.provider ? truncateText(row.provider.name, 20) : "--" }}
                                    </strong>
                                    <div v-if="row.provider && row.provider.cnpj">{{ row.provider.cnpj }}</div>
                                </div>
                            </template>
                            <template slot="seller" slot-scope="text, row">
                                <div class="col-vendedor" :title="row.seller && row.seller.name">
                                    {{ row.seller ? truncateText(row.seller.name, 14) : "--" }}
                                    <div v-if="row.seller && row.seller.phone" class="col-vendedor-zap">
                                        <i class="fab fa-whatsapp col-vendedor-zap-icon"></i>
                                        <a class="col-vendedor-zap-link" :href="whatsappLink(row.seller.phone)" target="_blank">
                                            {{ formatPhone(row.seller.phone) }}
                                        </a>
                                    </div>
                                </div>
                            </template>
                            <template slot="visit" slot-scope="text, row">
                                <span v-if="row.checkin_id">#{{ row.checkin_id }}</span>
                                <span v-else class="sup-v">Avulsa</span>
                            </template>
                            <template slot="status" slot-scope="text, row">
                                <a-tag :color="statusOf(row.status).color">{{ statusOf(row.status).label }}</a-tag>
                                <a-tag v-if="stockSurveyOf(row)" :color="stockSurveyOf(row).color">{{ stockSurveyOf(row).label }}</a-tag>
                            </template>
                            <template slot="acoes" slot-scope="text, row">
                                <ActionMenu :actions="actions(row)" />
                            </template>
                        </a-table>
                    </div>
                </a-card>
            </div>
        </div>

        <SelectionFooter :count="selectedIds.length" :item-label="selectedIds.length === 1 ? 'requisição selecionada' : 'requisições selecionadas'">
            <template slot="actions">
                <a-button size="large" :disabled="cancelling" @click="clearSelection">Limpar seleção</a-button>
                <a-button size="large" type="danger" :loading="cancelling" :disabled="!cancelableSelected.length" @click="cancelSelected">
                    {{ cancelableSelected.length > 1 ? "Cancelar requisições" : "Cancelar requisição" }}
                </a-button>
            </template>
        </SelectionFooter>

        <NewVisitModal v-if="showNew" kind="requisition" :stores="options.stores || []" @created="openRequisition" @close="showNew = false" />
    </div>
</template>

<script>
import SellerOrderService from "@/services/v3/seller-orders/seller-order.service";
import ActionMenu from "@/components/popovers/action-menu.popover.vue";
import SelectionFooter from "@/components/cliente/monitors/SelectionFooter.vue";
import VisitFilters from "@/pages/visits/components/visit-filters.vue";
import NewVisitModal from "@/pages/visits/modals/new-visit.modal.vue";
import { loaderMixin } from "@/mixins/sweet-loader.mixin";
import visitFormat from "@/pages/visits/visit-format.mixin";
import { visitListMixin } from "@/pages/visits/visit-list.mixin";
import { SELLER_ORDER_STATUS, ALL_SELLER_ORDER_STATUS, CANCELABLE_STATUSES, STOCK_SURVEY, isStockSurvey } from "./seller-order-status";
import { has_feature, VISITS } from "@/helpers/features";

export default {
    name: "SellerOrdersPage",
    components: { ActionMenu, VisitFilters, NewVisitModal, SelectionFooter },
    mixins: [visitFormat, visitListMixin, loaderMixin],
    data() {
        return {
            service: new SellerOrderService(),
            statusMap: { ...SELLER_ORDER_STATUS, DRAFT: ALL_SELLER_ORDER_STATUS.DRAFT },
            nouns: ["requisição", "requisições"],
            loadError: "Não foi possível carregar as requisições.",
            seenProviders: [],
            seenSellers: [],
            showNew: false,
            selected: {},
            cancelling: false,
        };
    },
    watch: {
        appliedFilters() {
            this.clearSelection();
        },
        rows(list) {
            list.filter((row) => row.id in this.selected).forEach((row) => this.$set(this.selected, row.id, row));
        },
    },
    computed: {
        selectedIds() {
            return Object.keys(this.selected).map(Number);
        },
        cancelableSelected() {
            return Object.values(this.selected).filter((row) => CANCELABLE_STATUSES.includes(row.status));
        },
        rowSelection() {
            return {
                selectedRowKeys: this.selectedIds,
                onSelect: (row, checked) => this.toggle(row, checked),
                onSelectAll: (checked, _rows, changed) => changed.forEach((row) => this.toggle(row, checked)),
            };
        },
        columns() {
            const sortable = (key) => ({ key, sorter: true, sortDirections: ["descend", "ascend"], sortOrder: this.sortOrderFor(key) });
            return [
                { title: "Requisição", ...sortable("id"), customRender: (t, row) => `#${row.id}` },
                { title: "Visita", key: "visit", align: "center", scopedSlots: { customRender: "visit" } },
                { title: "Enviada em", ...sortable("sent_at"), customRender: (t, row) => this.formatDateTime(row.sent_at) },
                { title: "Fornecedor", key: "provider", width: 150, scopedSlots: { customRender: "provider" } },
                { title: "Vendedor", key: "seller", width: 150, scopedSlots: { customRender: "seller" } },
                { title: "Loja", key: "store", customRender: (t, row) => (row.store ? row.store.name : "--") },
                { title: "Produtos", key: "offered", align: "center", customRender: (t, row) => this.int(row.totals.offered) },
                { title: "Valor", key: "amount", align: "right", customRender: (t, row) => this.brl(row.totals.amount) },
                { title: "Status", ...sortable("status"), scopedSlots: { customRender: "status" } },
                { title: "Decidida em", ...sortable("decided_at"), customRender: (t, row) => this.formatDateTime(row.decided_at) },
                { title: "Ação", key: "acoes", align: "center", fixed: "right", width: 70, scopedSlots: { customRender: "acoes" } },
            ];
        },
    },
    methods: {
        filterDefinitions() {
            return [
                { key: "visit", type: "text", digits: true, label: "Nº da visita", param: "checkin_id" },
                { key: "sent", type: "period", label: "Enviada em", params: ["sent_at_from", "sent_at_to"] },
                { key: "store", type: "select", numeric: true, label: "Loja", param: "store_id", options: () => this.options.stores || [], dropdownWidth: "300px" },
                { key: "status", type: "select", label: "Status", param: "status", options: () => this.statusOptions },
                { key: "id", type: "text", digits: true, label: "Nº da requisição", param: "id" },
                { key: "providers", type: "select", multiple: true, numeric: true, label: "Fornecedores", param: "provider_id", options: () => this.seenProviders, dropdownWidth: "500px", advanced: true },
                { key: "sellers", type: "select", multiple: true, numeric: true, label: "Vendedores", param: "seller_id", options: () => this.seenSellers, dropdownWidth: "300px", advanced: true },
                { key: "offered", type: "range", label: "Produtos", params: ["offered_min", "offered_max"], advanced: true },
                { key: "amount", type: "range", label: "Valor (R$)", params: ["amount_min", "amount_max"], advanced: true },
                { key: "decided", type: "period", label: "Decidida em", params: ["decided_at_from", "decided_at_to"], advanced: true },
            ];
        },
        collectOptions(rows) {
            const merge = (list, item) => {
                if (item && !list.some((option) => option.value === item.id)) list.push({ value: item.id, label: item.name });
            };
            rows.forEach((row) => {
                merge(this.seenProviders, row.provider);
                merge(this.seenSellers, row.seller);
            });
        },
        openRequisition(requisition) {
            this.$router.push(`/requisicoes/${requisition.id}`);
        },
        stockSurveyOf(row) {
            return isStockSurvey(row) ? STOCK_SURVEY : null;
        },
        actions(row) {
            return [
                {
                    label: row.status === "DRAFT" ? "Editar produtos" : "Ver detalhes",
                    icon: row.status === "DRAFT" ? "edit" : "visibility",
                    onClick: () => this.$router.push(`/requisicoes/${row.id}`),
                },
                row.checkin_id && has_feature(VISITS) && { label: "Ver visita", icon: "storefront", onClick: () => this.$router.push(`/visitas/${row.checkin_id}`) },
                CANCELABLE_STATUSES.includes(row.status) && { label: "Cancelar requisição", icon: "cancel", onClick: () => this.cancel(row) },
            ].filter(Boolean);
        },
        toggle(row, checked) {
            if (checked) this.$set(this.selected, row.id, row);
            else this.$delete(this.selected, row.id);
        },
        clearSelection() {
            this.selected = {};
        },
        cancelSelected() {
            const rows = this.cancelableSelected;
            const kept = this.selectedIds.length - rows.length;
            this.confirm_action({
                message: `Cancelar ${rows.length} ${rows.length === 1 ? "requisição" : "requisições"}?`,
                subtitle: [
                    "Elas continuam na lista como canceladas e não podem mais ser alteradas nem aprovadas.",
                    kept ? `${kept} ${kept === 1 ? "selecionada já foi decidida e será mantida" : "selecionadas já foram decididas e serão mantidas"}.` : null,
                ]
                    .filter(Boolean)
                    .join(" "),
                confirm_text: "Sim, cancelar",
                cancel_text: "Voltar",
                callback: async () => {
                    this.cancelling = true;
                    let failed = 0;
                    for (const row of rows) {
                        try {
                            await this.service.cancel(row.id);
                            this.$delete(this.selected, row.id);
                        } catch (error) {
                            failed += 1;
                            if (failed === 1) this.notifyError(error, "Não foi possível cancelar todas as requisições.");
                        }
                    }
                    this.cancelling = false;
                    if (!failed) {
                        this.snack(rows.length === 1 ? "Requisição cancelada." : "Requisições canceladas.");
                        this.clearSelection();
                    }
                    this.load(this.meta.current_page);
                },
            });
        },
        cancel(row) {
            this.confirm_action({
                message: `Cancelar a requisição #${row.id}?`,
                subtitle: "Ela continua na lista como cancelada e não pode mais ser alterada nem aprovada.",
                confirm_text: "Sim, cancelar",
                cancel_text: "Voltar",
                callback: () => {
                    this.service
                        .cancel(row.id)
                        .then(() => {
                            this.snack("Requisição cancelada.");
                            this.load(this.meta.current_page);
                        })
                        .catch((error) => this.notifyError(error, "Não foi possível cancelar a requisição."));
                },
            });
        },
    },
};
</script>

<style lang="scss" scoped>
@import "@/pages/visits/_page";
</style>
